import { JWMHeaderHero } from "@components/JWMHeaderHero/JWMHeaderHero";
import { JWMHero } from "@components/JWMHero/JWMHero";
import { Hero } from "@jmc/solid-design-system/src/components/atoms/Hero/Hero";
import {
    Color,
    HeaderHero,
    Size,
    Variant,
} from "@jmc/solid-design-system/src/components/molecules/HeaderHero/HeaderHero";
import { CMSColor } from "@jwm/types/CMSColor";
import { CMSHeroProps } from "@jwm/types/CMSHeroProps";

import { HeroColor, heroColorMap } from "./CMSMapperDefinitions";
import { mapButtonProps } from "./mapButtonProps";

const mapHeroColors = (cmsColor: CMSColor): HeroColor => {
    return heroColorMap.get(cmsColor);
};

/**
 * transform a CMSHeroProps instance into props for a Hero component
 */
export const mapHeroProps = (cmsProps: CMSHeroProps): React.ComponentProps<typeof Hero> => {
    const imageData = cmsProps?.background_image?.gatsbyImageData;
    return {
        id: cmsProps?.display_title,
        title: cmsProps?.display_title,
        image: imageData,
        imagePosition: cmsProps?.display_options?.image_focus?.toLowerCase() || "center-center",
        fullWidth: cmsProps?.display_options?.full_width,
        contentAlignment: cmsProps?.display_options?.align_content?.toLowerCase(),
        contentVerticalAlignment: cmsProps?.display_options?.align_content_vertically?.toLowerCase(),
        size: cmsProps?.display_options?.size,
        hasOverlay: cmsProps?.display_options?.has_overlay,
        backgroundColor: imageData
            ? "transparent"
            : cmsProps?.display_options?.background_color !== null
              ? mapHeroColors(cmsProps?.display_options?.background_color as CMSColor)
              : "primary",
        titleColor:
            cmsProps?.display_options?.title_color !== null
                ? mapHeroColors(cmsProps?.display_options?.title_color as CMSColor)
                : "white", // --text-primary is text-dark.
        textColor:
            cmsProps?.display_options?.text_color !== null
                ? mapHeroColors(cmsProps?.display_options?.text_color as CMSColor)
                : "white",
        subtitleColor:
            cmsProps?.display_options?.sub_title_reference_and_link_color &&
            cmsProps?.display_options?.sub_title_reference_and_link_color?.toLowerCase() !== "default"
                ? mapHeroColors(cmsProps?.display_options?.sub_title_reference_and_link_color as CMSColor)
                : "--primary",
        linkColor:
            cmsProps?.display_options?.sub_title_reference_and_link_color &&
            cmsProps?.display_options?.sub_title_reference_and_link_color?.toLowerCase() !== "default"
                ? mapHeroColors(cmsProps?.display_options?.sub_title_reference_and_link_color as CMSColor)
                : "--primary",
        section_visual: cmsProps?.display_options?.section_visual?.section_visual_source?.url,
        position_horizontal: cmsProps?.display_options?.section_visual?.position_horizontal || "left",
        position_vertical: cmsProps?.display_options?.section_visual?.position_vertical || "top",
    };
};

/**
 * transform a CMSHeroProps instance into props for a HeaderHero component
 */
export const mapHeaderHeroProps = (cmsProps: CMSHeroProps): React.ComponentProps<typeof HeaderHero> => {
    const imageData = cmsProps?.background_image?.gatsbyImageData;
    const color = cmsProps?.display_options?.background_color?.toLowerCase() as Color;
    const variant = cmsProps?.display_options?.variant;

    return {
        id: cmsProps?.display_title,
        title: cmsProps?.display_title,
        image: imageData,
        imagePosition: cmsProps?.display_options?.image_focus?.toLowerCase() || "center-center",
        size: cmsProps?.display_options?.size as Size,
        color: Object.values(Color).includes(color) ? color : undefined,
        variant: Object.values(Variant).includes(variant) ? variant : undefined,
    };
};

/**
 * transform a CMSHeroProps instance into props for a JWMHero component
 */
export const mapJWMHeroProps = (cmsProps: CMSHeroProps): React.ComponentProps<typeof JWMHero> => {
    const buttonProps = cmsProps?.call_to_action?.map((item) => mapButtonProps(item.button));
    const buttonPropsCarousel = cmsProps?.carousel?.slides?.map((slide) => mapButtonProps(slide.button));

    return {
        ...mapHeroProps(cmsProps),
        description: cmsProps?.hero_body,
        footer: buttonProps?.length > 0 ? buttonProps : buttonPropsCarousel,
        interval: { auto: cmsProps?.carousel?.interval?.automatic, speed: cmsProps?.carousel?.interval?.speed * 1000 },
    };
};

/**
 * transform a CMSHeroProps instance into props for a JWMHeaderHero component
 */
export const mapJWMHeaderHeroProps = (cmsProps: CMSHeroProps): React.ComponentProps<typeof JWMHeaderHero> => {
    const buttonProps = cmsProps?.call_to_action?.map((item) => mapButtonProps(item.button));

    return {
        ...mapHeaderHeroProps(cmsProps),
        description: cmsProps?.hero_body,
        footer: buttonProps,
    };
};
